import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
// services
import { MasterService } from "../../../services/master.service";
import { MessageService } from "../../../services/message.service";
import { StoreService } from "../../../services/store.service";
import { SecurityService } from "src/app/services/security.service";
import * as languageLibrary from '../../../services/language';
import { Lot } from "src/app/models/lot.model";
import moment from "moment";
import { Subscription } from "rxjs";
import { Receipt } from "src/app/models/receipt.model";
import { DATE_DEBOUNCE_MILLIS } from "src/app/constants/list";
import { SalesTaxSummaryEntry } from "src/app/models/salestax-summary-entry.model";
import { SalesTaxSummaryGroup } from "src/app/models/salestax-summary-group.model";

@Component({
    selector: "salestax-panel",
    templateUrl: "./salestax-panel.component.html",
    styleUrls: ["./salestax-panel.component.scss"],
    standalone: false
})
export class SalesTaxPanelComponent implements OnInit, OnDestroy {

    /** define if is loading */
    protected loading: boolean = false

    // save the language
    protected language: string | null = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    // set all words
    protected words = languageLibrary.language

    /** */
    protected lots: Lot[] = []

    // The search form group
    protected form: FormGroup | null = null

    // Grouped sales tax summary data for display in the panel.
    protected grouped: SalesTaxSummaryGroup | null = null

    // A debounce timer for search events. Used to wait for
    // the user to stop typing before we fetch.
    private watcher: ReturnType<typeof setTimeout> | null = null

    /** Listen for changes to the lot filter. */
    private subscriptions: Subscription[] = []

    /** If the report is filtered by lot this will have a positive value. */
    protected get lotId() {
        return parseInt(this.form.get('lotId').value)
    }

    /** A human readable date span based on the user selections. */
    protected get dateSpan() {
        const from = moment(this.form.get('from').value)
        const to = moment(this.form.get('to').value)

        const span = `${from.format('ddd MMM Do, YYYY')}`

        if (to.isSameOrBefore(from)) {
            return span
        }

        return `${span} - ${to.format('ddd MMM Do, YYYY')}`
    }

    constructor(private master: MasterService, private ms: MessageService, protected store: StoreService, private router: Router, private fb: FormBuilder, protected security: SecurityService) {
        this.form = this.fb.group({
            lotId: new FormControl<number>(0, []),
            from: new FormControl<string>(moment().startOf('quarter').subtract(3, 'months').format('YYYY-MM-DD'), []),
            to: new FormControl<string>(moment().startOf('quarter').subtract(1, 'days').format('YYYY-MM-DD'), [])
        })
    }

    ngOnInit() {
        this.subscriptions.push(
            this.form.get('lotId').valueChanges.subscribe((id) => {
                this.reset()
                this.fetch()
            })
        )

        this.subscriptions.push(
            this.form.get('from').valueChanges.subscribe((from) => {
                if (this.watcher) {
                    clearTimeout(this.watcher)
                    this.watcher = null
                }

                this.watcher = setTimeout(() => {
                    this.reset()
                    this.fetch()
                }, DATE_DEBOUNCE_MILLIS)
            })
        )

        this.subscriptions.push(
            this.form.get('to').valueChanges.subscribe((to) => {
                if (this.watcher) {
                    clearTimeout(this.watcher)
                    this.watcher = null
                }

                this.watcher = setTimeout(() => {
                    this.reset()
                    this.fetch()
                }, DATE_DEBOUNCE_MILLIS)
            })
        )

        this.reset()
        this.fetch()

        if (this.store && this.store.userAccount && this.store.userAccount['user'] &&
            this.store.userAccount['user'].lots) {
            this.lots = this.store.userAccount['user'].lots
        }
    }

    ngOnDestroy() {
        this.reset()

        if (this.watcher) {
            clearTimeout(this.watcher)
            this.watcher = null
        }

        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
    }

    /**
     * Reset the pagination parameters and scroll to the top
    */
    private reset() {
        this.grouped = null
        
        const list = document.querySelector('#listing')
        if (!list) {
            return
        }

        list.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto'
        })
    }

    /**
     * Get the summary and list of receivables needed for the report.
     */
    protected fetch = async () => {
        if (this.security.notPermitted('reports', 'cash flow', 'view')) {
            return
        }

        if (this.loading) { // already getting the data
            return
        }

        let lotParam = ''
        const id = parseInt(this.form.get('lotId').value)
        let prefix = '?'
        if (id && (!isNaN(id) && (id > 0))) {
            lotParam = `?lot=${id}`
            prefix = '&'
        }

        let fromParam = ''
        const from = this.form.get('from').value
        if (from) {
            fromParam = `${prefix}from=${moment(from).startOf('day').format()}`
            prefix = '&'
        }

        let toParam = ''
        const to = this.form.get('to').value
        if (to) {
            toParam = `${prefix}to=${moment(to).endOf('day').format()}`
        }

        this.loading = true
        await this.master.getAsync(`reports/salestax${lotParam}${fromParam}${toParam}`).then(res => {
            this.loading = false

            if (!res || !res.data) {
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error ? res.data.error : this.words[this.language]['apiNoResponse'] });
                return
            }

            this.constructGroups(res.data.summary)
        })
    }

    private constructGroups = (summary: Array<SalesTaxSummaryEntry>) => {
        if (!summary || (summary.length <= 0)) {
            return
        }

        this.grouped = {
            name: summary[0].name,
            address: summary[0].address,
            cityStateZip: summary[0].cityStateZip,
            dealerNumber: summary[0].dealerNumber,
            einNumber: summary[0].einNumber,
            initial: {
                collected: 0,
                taxable: 0,
                tax: 0,
                itemized: 0,
                deferred: {
                    collected: 0,
                    taxable: 0,
                    tax: 0,
                    entries: new Array<SalesTaxSummaryEntry>()
                },
                paid: {
                    collected: 0,
                    taxable: 0,
                    tax: 0,
                    entries: new Array<SalesTaxSummaryEntry>()
                },
                outside: {
                    collected: 0,
                    taxable: 0,
                    tax: 0,
                    entries: new Array<SalesTaxSummaryEntry>()
                }
            },
            deposits: {
                collected: 0,
                taxable: 0,
                tax: 0,
                deferred: {
                    collected: 0,
                    taxable: 0,
                    tax: 0,
                    entries: new Array<SalesTaxSummaryEntry>()
                },
                paid: {
                    collected: 0,
                    taxable: 0,
                    tax: 0,
                    entries: new Array<SalesTaxSummaryEntry>()
                },
                outside: {
                    collected: 0,
                    taxable: 0,
                    tax: 0,
                    entries: new Array<SalesTaxSummaryEntry>()
                }
            },
            bhph: {
                collected: 0,
                taxable: 0,
                tax: 0,
                deferred: {
                    collected: 0,
                    taxable: 0,
                    tax: 0,
                    entries: new Array<SalesTaxSummaryEntry>()
                },
                paid: {
                    collected: 0,
                    taxable: 0,
                    tax: 0,
                    entries: new Array<SalesTaxSummaryEntry>()
                }
            },
            outside: {
                collected: 0,
                taxable: 0,
                tax: 0,
                entries: new Array<SalesTaxSummaryEntry>()
            },
            financed: {
                collected: 0,
                taxable: 0,
                tax: 0,
                entries: null
            },
            cash: {
                collected: 0,
                taxable: 0,
                tax: 0,
                entries: new Array<SalesTaxSummaryEntry>()
            },
            wholesale: {
                collected: 0,
                taxable: 0,
                tax: 0,
                entries: new Array<SalesTaxSummaryEntry>()
            },
            after: {
                collected: 0,
                taxable: 0,
                tax: 0,
                entries: new Array<SalesTaxSummaryEntry>()
            },
            total: {
                collected: 0,
                taxable: 0,
                tax: 0,
                entries: null
            }
        }

        for (const payment of summary) {
            this.grouped.initial.collected += payment.initial + payment.initialOutside
            this.grouped.initial.taxable += ((payment.initialTax + payment.initialOutsideTax) / payment.taxRate)
            this.grouped.initial.tax += payment.initialTax + payment.initialOutsideTax
            if (payment.tax && (payment.tax > 0)) {
                this.grouped.initial.itemized += payment.itemized
            }
            this.grouped.deposits.collected += payment.deposits + payment.depositsOutside
            this.grouped.deposits.taxable += ((payment.depositTax + payment.depositsOutsideTax) / payment.taxRate)
            this.grouped.deposits.tax += payment.depositTax + payment.depositsOutsideTax
            this.grouped.after.collected += payment.afterSale

            this.grouped.financed.collected += payment.initial + payment.initialOutside + payment.deposits + payment.depositsOutside
            this.grouped.financed.taxable += ((payment.initialTax + payment.initialOutsideTax) / payment.taxRate) + ((payment.depositTax + payment.depositsOutsideTax) / payment.taxRate)
            this.grouped.financed.tax += payment.initialTax + payment.initialOutsideTax + payment.depositTax + payment.depositsOutsideTax
            this.grouped.total.collected += payment.deposits + payment.depositsOutside + payment.initial + payment.initialOutside + payment.afterSale
            this.grouped.total.taxable += ((payment.initialTax + payment.initialOutsideTax) / payment.taxRate) + ((payment.depositTax + payment.depositsOutsideTax) / payment.taxRate)
            this.grouped.total.tax += payment.initialTax + payment.initialOutsideTax + payment.depositTax + payment.depositsOutsideTax

            if (payment.category === 'initial down') {
                switch (payment.saleType) {
                    case 'bhph':
                        if (payment.taxMethod && (payment.taxMethod.toLowerCase() === 'deferred')) {
                            this.grouped.initial.deferred.collected += payment.initial
                            this.grouped.initial.deferred.taxable += (payment.initialTax / payment.taxRate)
                            this.grouped.initial.deferred.tax += payment.initialTax
                            this.grouped.initial.deferred.entries.push(payment)
                            break
                        }

                        this.grouped.initial.paid.collected += payment.initial
                        this.grouped.initial.paid.taxable += (payment.initialTax / payment.taxRate)
                        this.grouped.initial.paid.tax += payment.initialTax
                        this.grouped.initial.paid.entries.push(payment)
                        break
                    case 'outside':
                        this.grouped.initial.outside.collected += payment.initialOutside
                        this.grouped.initial.outside.taxable += (payment.initialOutsideTax / payment.taxRate)
                        this.grouped.initial.outside.tax += payment.initialOutsideTax
                        this.grouped.initial.outside.entries.push(payment)
                        break
                }
            }

            if (payment.category === 'deposit') {
                switch (payment.saleType) {
                    case 'bhph':
                        if (payment.taxMethod && (payment.taxMethod.toLowerCase() === 'deferred')) {
                            this.grouped.deposits.deferred.collected += payment.deposits
                            this.grouped.deposits.deferred.taxable += (payment.depositTax / payment.taxRate)
                            this.grouped.deposits.deferred.tax += payment.depositTax
                            this.grouped.deposits.deferred.entries.push(payment)
                            break
                        }

                        this.grouped.deposits.paid.collected += payment.deposits
                        this.grouped.deposits.paid.taxable += (payment.depositTax / payment.taxRate)
                        this.grouped.deposits.paid.tax += payment.depositTax
                        this.grouped.deposits.paid.entries.push(payment)
                        break
                    case 'outside':
                        this.grouped.deposits.outside.collected += payment.depositsOutside
                        this.grouped.deposits.outside.taxable += (payment.depositsOutsideTax / payment.taxRate)
                        this.grouped.deposits.outside.tax += payment.depositsOutsideTax
                        this.grouped.deposits.outside.entries.push(payment)
                        break
                }
            }

            if (payment.category === 'after-sale charges') {
                this.grouped.after.entries.push(payment)
            }

            switch (payment.saleType) {
                case 'bhph':
                    this.grouped.financed.collected += payment.bhph
                    this.grouped.total.collected += payment.bhph
                    this.grouped.bhph.collected += payment.bhph + payment.lates + payment.bounce

                    if (payment.taxMethod && payment.taxMethod.toLowerCase() === 'deferred' && payment.bhph > 0) {
                        this.grouped.bhph.deferred.collected += payment.bhph + payment.lates + payment.bounce
                        this.grouped.bhph.deferred.taxable += (payment.tax / payment.taxRate)
                        this.grouped.bhph.deferred.tax += payment.tax
                        
                        this.grouped.bhph.taxable += (payment.tax / payment.taxRate)
                        this.grouped.bhph.tax += payment.tax

                        this.grouped.financed.taxable += (payment.tax / payment.taxRate)
                        this.grouped.financed.tax += payment.tax

                        this.grouped.total.taxable += (payment.tax / payment.taxRate)
                        this.grouped.total.tax += payment.tax

                        if (['mv loan', 'deferred down'].includes(payment.category)) {
                            this.grouped.bhph.deferred.entries.push(payment)
                        }

                        break
                    }

                    this.grouped.bhph.paid.collected += payment.bhph + payment.lates + payment.bounce
                    if (['mv loan', 'deferred down'].includes(payment.category)) {
                        this.grouped.bhph.paid.entries.push(payment)
                    }

                    break
                case 'outside':
                    this.grouped.financed.collected += payment.outside
                    this.grouped.total.collected += payment.outside
                    this.grouped.outside.collected += payment.outside
                    if (payment.category === 'drive-out price') {
                        this.grouped.outside.entries.push(payment)
                    }

                    break
                case 'cash':
                    this.grouped.total.collected += payment.cash
                    this.grouped.cash.collected += payment.cash
                    if (payment.category === 'drive-out price') {
                        this.grouped.cash.entries.push(payment)
                    }

                    break
                case 'wholesale':
                    this.grouped.total.collected += payment.wholesale
                    this.grouped.wholesale.collected += payment.wholesale
                    if (payment.category === 'drive-out price') {
                        this.grouped.wholesale.entries.push(payment)
                    }

                    break
            }
        }
    }

    /**
     * listen to output of navbar
    */
    protected listenerNav = (e): void => {
        switch (e['message']) {
            case 'changeLanguage':
                this.language = e['value']
                break
            case 'setPermissions':
                this.lots = this.store.userAccount['user'].lots
                this.reset()
                this.fetch()
                break
            case 'changeLot':
                break
        }
    }
}
